import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { IMAGES } from "@/data/site";
import { PageHero, SectionHeader } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import CompanyProfile from "@/components/CompanyProfile";
import HeroShowcase from "@/components/HeroShowcase";
import { Sun, Mountain, Clapperboard, ArrowUpRight, ShieldCheck, TrendingUp, Leaf, Building2, Handshake, Layers, Target } from "lucide-react";

const PILLARS = [
  { id: "solar", icon: Sun, img: IMAGES.solarHero, path: "/solar" },
  { id: "mining", icon: Mountain, img: IMAGES.miningHero, path: "/mining" },
  { id: "media", icon: Clapperboard, img: IMAGES.mediaHero, path: "/media" },
];

const WHY = [
  { icon: ShieldCheck, en: ["Bankable & Compliant", "ISO 27001 certified operations with full MCA, CERC & MPERB regulatory alignment."], hi: ["बैंक योग्य एवं अनुपालक", "पूर्ण MCA, CERC एवं MPERB नियामक संरेखण के साथ ISO 27001 प्रमाणित संचालन।"] },
  { icon: TrendingUp, en: ["Growth-Oriented", "Disciplined capital allocation across high-yield, long-duration infrastructure assets."], hi: ["विकास-उन्मुख", "उच्च-प्रतिफल, दीर्घकालिक अवसंरचना परिसंपत्तियों में अनुशासित पूंजी आवंटन।"] },
  { icon: Leaf, en: ["Sustainability First", "Carbon-negative energy portfolio with zero-runoff mining and circular resource use."], hi: ["स्थिरता प्रथम", "शून्य-अपवाह खनन एवं चक्रीय संसाधन उपयोग के साथ कार्बन-नकारात्मक ऊर्जा पोर्टफोलियो।"] },
  { icon: Layers, en: ["Diversified Platform", "Three synergistic verticals de-risking revenue across energy, minerals and media."], hi: ["विविधीकृत मंच", "ऊर्जा, खनिज एवं मीडिया में राजस्व को विविधीकृत करने वाले तीन समन्वयकारी क्षेत्र।"] },
];

export default function Home() {
  const { t, lang, content } = useApp();
  const hero = content.hero?.[lang] || {};
  const stats = content.stats || {};

  const statKeys = ["solar_capacity", "co2_offset", "projects", "years"];
  const vmCards = [
    { key: "vision", icon: Target, label: t.home.visionLabel, text: t.home.visionText },
    { key: "mission", icon: Building2, label: t.home.missionLabel, text: t.home.missionText },
  ];

  return (
    <div>
      {/* HERO */}
      <PageHero image={IMAGES.solarHero} video="/assets/hero-solar-aerial.mp4" poster="/assets/hero-solar-poster.jpg" tall top eyebrow={hero.eyebrow} title={hero.title || t.pillars.title} subtitle={hero.subtitle} showcase={<HeroShowcase />}>
        <Link to="/solar" className="oj-btn-gold" data-testid="hero-explore-btn">{hero.cta || t.common.explore} <ArrowUpRight className="w-4 h-4" /></Link>
        <Link to="/contact" className="oj-btn-ghost" data-testid="hero-partner-btn"><Handshake className="w-4 h-4" /> {hero.cta2 || t.home.partner}</Link>
        <div className="basis-full flex flex-wrap gap-2 mt-1" data-testid="hero-trust-badges">
          {t.home.badges.map((b) => (
            <span key={b} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-white/10 text-teal-100 border border-white/25 backdrop-blur">
              <ShieldCheck className="w-3.5 h-3.5 text-amber-300" /> {b}
            </span>
          ))}
        </div>
      </PageHero>

      {/* STATS STRIP */}
      <Section sid="home.stats" name="Stats Strip" className="border-b border-teal-900/40 bg-[#E9F5F4]/40">
        <div className="oj-container grid grid-cols-2 lg:grid-cols-4 divide-x divide-teal-900/40">
          {statKeys.map((k, i) => (
            <Reveal key={k} delay={i * 0.08} className="py-8 px-4 text-center">
              <div className="text-3xl sm:text-4xl font-extrabold gold-text font-mono">{stats[k] || "—"}</div>
              <div className="mt-1 text-xs sm:text-sm text-slate-600">{t.home.statLabels[k]}</div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* COMPANY PROFILE */}
      <CompanyProfile />

      {/* PILLARS */}
      <Section sid="home.pillars" name="Business Pillars" className="oj-section">
        <div className="oj-container">
          <SectionHeader eyebrow={t.home.ecosystem} title={t.pillars.title} subtitle={t.pillars.subtitle} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {PILLARS.map((p, i) => {
              const Icon = p.icon; const data = t.pillars[p.id];
              return (
                <Reveal key={p.id} delay={i * 0.1}>
                  <Link to={p.path} data-testid={`pillar-${p.id}`} className="group block oj-card overflow-hidden h-full">
                    <div className="relative h-52 overflow-hidden">
                      <img src={p.img} alt={data.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#E9F5F4] via-[#E9F5F4]/40 to-transparent" />
                      <div className="absolute top-4 left-4 grid place-items-center w-11 h-11 rounded-xl bg-[#FFFFFF]/80 border border-amber-400/40 backdrop-blur">
                        <Icon className="w-5 h-5 text-amber-400" />
                      </div>
                      <span className="absolute bottom-4 left-4 px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider bg-teal-950/80 text-teal-200 border border-teal-400/30">{data.stat}</span>
                    </div>
                    <div className="p-6">
                      <h3 className="oj-h3 group-hover:text-amber-300 transition-colors">{data.title}</h3>
                      <p className="mt-3 text-sm text-slate-600 leading-relaxed">{data.desc}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600">{t.common.learnMore} <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </Section>

      {/* VISION / MISSION */}
      <Section sid="home.vm" name="Vision & Mission" className="relative overflow-hidden bg-[#E9F5F4]/40 border-y border-teal-900/40">
        <div className="oj-container oj-section grid grid-cols-1 lg:grid-cols-2 gap-8">
          {vmCards.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.key} delay={i * 0.12}>
                <div className="oj-card p-8 h-full transition-all duration-300 hover:-translate-y-1.5 hover:!border-amber-400/60 hover:shadow-2xl hover:shadow-teal-900/20" data-testid={`home-${c.key}-card`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="grid place-items-center w-11 h-11 rounded-xl bg-amber-100 border border-amber-300"><Icon className="w-5 h-5 text-amber-600" /></div>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0B3B39]">{c.label}</h3>
                  </div>
                  <div className="h-1 w-14 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 mb-4" />
                  <p className="text-slate-700 leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* WHY US */}
      <Section sid="home.why" name="Why Us" className="oj-section">
        <div className="oj-container">
          <SectionHeader eyebrow={t.home.instAdv} title={t.home.whyTitle} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w, i) => {
              const Icon = w.icon; const [title, desc] = w[lang];
              return (
                <Reveal key={w.en[0]} delay={i * 0.08}>
                  <div className="oj-card p-6 h-full">
                    <div className="grid place-items-center w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-400/30 mb-4"><Icon className="w-6 h-6 text-amber-400" /></div>
                    <h4 className="font-bold text-white mb-2">{title}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </Section>

      {/* PARTNERSHIP CTA */}
      <Section sid="home.cta" name="Partnership CTA" className="oj-container pb-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-amber-400/20 grain">
            <img src={IMAGES.building} alt="corporate" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#014845] via-[#015E5C]/90 to-[#017471]/75" />
            <div className="relative p-10 sm:p-14 max-w-2xl on-dark">
              <div className="oj-eyebrow mb-3">{t.home.partnersTitle}</div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">{t.home.ctaBanner}</h2>
              <p className="mt-4 text-teal-50/90 leading-relaxed">{t.home.partnersDesc}</p>
              <div className="mt-7 flex flex-wrap gap-4">
                <Link to="/contact" className="oj-btn-gold" data-testid="cta-partner-btn"><Handshake className="w-4 h-4" /> {t.home.initiate}</Link>
                <Link to="/investors" className="oj-btn-ghost" data-testid="cta-investor-btn">{t.home.investorRel} <ArrowUpRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
